import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../LanguageContext";
import { DonateButton } from "./DonateButton";

const navItems = [
  { to: "/", key: "home" },
  { to: "/comprendre", key: "informer" },
  { to: "/s-engager", key: "engager" },
  { to: "/inscrire", key: "inscrire" },
  { to: "/actualites", key: "news" },
  { to: "/formation", key: "formation" },
  { to: "/contact", key: "contact" },
  { to: "/equipe", key: "team" },
];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 bg-cream-50/85 backdrop-blur-xl border-b border-cream-300/60"
      data-testid="site-navbar"
    >
      <div className="container-page flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img
            src="/logo.png"
            alt="École Pleine Nature"
            className="w-10 h-10 object-contain shrink-0"
          />
          <div className="leading-tight">
            <div className="font-heading text-[15px] font-semibold text-ink">
              École Pleine Nature
            </div>
            <div className="text-[11px] text-ink-muted tracking-wide">Ariège · démocratique</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `nav-link ${isActive ? "nav-link-active" : ""}`
              }
              data-testid={`nav-${item.key}`}
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-forest px-2.5 py-1.5 rounded-full border border-cream-300"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            <span className="uppercase tracking-wider">{lang === "fr" ? "EN" : "FR"}</span>
          </button>
          <Link
            to="/espace-membres"
            className="hidden md:inline-flex text-xs font-medium text-forest border border-forest/30 rounded-full px-3.5 py-1.5 hover:bg-forest hover:text-cream-50 transition-colors"
            data-testid="nav-members"
          >
            {t("nav.members")}
          </Link>
          <DonateButton
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium bg-forest text-cream-50 rounded-full px-3.5 py-1.5 hover:bg-forest-dark transition-colors"
            testId="nav-donate"
          />
          <button
            className="lg:hidden p-2 rounded-md text-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-cream-300 bg-cream-50" data-testid="mobile-menu">
          <div className="container-page py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-2 py-2.5 rounded-md text-sm font-medium ${
                    isActive ? "text-forest bg-forest-light/50" : "text-ink-muted hover:text-forest"
                  }`
                }
                data-testid={`mobile-nav-${item.key}`}
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <Link
              to="/espace-membres"
              onClick={() => setOpen(false)}
              className="px-2 py-2.5 rounded-md text-sm font-medium text-forest"
              data-testid="mobile-nav-members"
            >
              {t("nav.members")}
            </Link>
            <DonateButton
              className="mt-1 inline-flex items-center justify-center gap-1.5 text-sm font-medium bg-forest text-cream-50 rounded-full px-4 py-2.5 self-start"
              testId="mobile-nav-donate"
              onClick={() => setOpen(false)}
            />
            <button
              type="button"
              className="self-start mt-2 inline-flex items-center gap-1.5 text-xs text-ink-muted px-2.5 py-1.5 rounded-full border border-cream-300"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              data-testid="mobile-lang-toggle"
            >
              <Globe size={14} /> {lang === "fr" ? "English" : "Français"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
